import { type Capture, CaptureSchema } from "./schema";
import type { KeyValueStorage } from "./storage";

// Journal des captures ABANDONNÉES par le filet de re-synchro (audio local
// définitivement introuvable). Vit HORS du buffer actif (finding QA resync #2) :
// - le buffer reste propre (fini la boucle de réessai et le spam de log) ;
// - la capture COMPLÈTE est conservée ici → preuve pour la notice usager
//   (titre/date), et réversibilité si un abandon s'avérait un faux positif
//   (restauration manuelle possible vers le buffer) ;
// - `seenAt` porte l'état « notice vue » : la bannière de l'accueil n'affiche
//   que les entrées jamais vues, le bouton OK les marque toutes vues.
const ABANDONED_KEY = "abandoned-captures";

// Événement fenêtre émis quand le filet de re-synchro vient d'abandonner des
// captures : la bannière de l'accueil (déjà montée à ce moment-là, le resync
// courant au montage) relit le journal sans attendre le prochain écran.
export const CAPTURES_ABANDONED_EVENT = "ecoute:captures-abandoned";

// Plafond anti-croissance infinie : au-delà, on retire d'abord la plus
// ancienne entrée déjà VUE (elle a rempli son rôle de notice), sinon la plus
// ancienne tout court.
export const MAX_ABANDONED_ENTRIES = 20;

export interface AbandonedEntry {
  capture: Capture;
  error: string;
  abandonedAt: string;
  seenAt: string | null;
}

export interface AbandonedCapturesStore {
  list(): Promise<AbandonedEntry[]>;
  unseen(): Promise<AbandonedEntry[]>;
  add(capture: Capture, error: string, nowIso: string): Promise<void>;
  markAllSeen(nowIso: string): Promise<void>;
}

// Une entrée relue du disque n'est gardée que si elle est complète et que sa
// capture passe le schéma (même politique que le buffer : on ignore le corrompu
// plutôt que de faire planter la lecture).
function parseEntry(item: unknown): AbandonedEntry | null {
  if (typeof item !== "object" || item === null) return null;
  const e = item as Partial<AbandonedEntry>;
  const capture = CaptureSchema.safeParse(e.capture);
  if (!capture.success || typeof e.error !== "string" || typeof e.abandonedAt !== "string")
    return null;
  return {
    capture: capture.data,
    error: e.error,
    abandonedAt: e.abandonedAt,
    seenAt: typeof e.seenAt === "string" ? e.seenAt : null,
  };
}

export function createAbandonedCapturesStore(kv: KeyValueStorage): AbandonedCapturesStore {
  async function readAll(): Promise<AbandonedEntry[]> {
    const raw = await kv.get(ABANDONED_KEY);
    if (!raw) return [];
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
    if (!Array.isArray(parsed)) return [];
    return parsed.map(parseEntry).filter((e): e is AbandonedEntry => e !== null);
  }

  async function writeAll(entries: AbandonedEntry[]): Promise<void> {
    await kv.set(ABANDONED_KEY, JSON.stringify(entries));
  }

  return {
    list: readAll,
    async unseen() {
      return (await readAll()).filter((e) => e.seenAt === null);
    },
    async add(capture, error, nowIso) {
      const entries = await readAll();
      entries.push({ capture, error, abandonedAt: nowIso, seenAt: null });
      while (entries.length > MAX_ABANDONED_ENTRIES) {
        const oldestSeen = entries.findIndex((e) => e.seenAt !== null);
        entries.splice(oldestSeen >= 0 ? oldestSeen : 0, 1);
      }
      await writeAll(entries);
    },
    async markAllSeen(nowIso) {
      const entries = await readAll();
      await writeAll(entries.map((e) => (e.seenAt === null ? { ...e, seenAt: nowIso } : e)));
    },
  };
}
